/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Store
import useStore from '@store/index';
import { useElementSize } from '@mantine/hooks';
import {
	TApiResponseProducerPopulated,
	TBodyDeleteMultiple,
	TParsedProducer,
	TResponseDeleteMultiple,
} from '../types';
import {
	TApiError,
	TProducer,
} from '@global-types/api/api';
import {
	TParams,
	TSelectOption,
	TSortStatus,
} from '@global-types/app/app';
import {
	UseMutateFunction,
	UseMutationResult,
} from '@tanstack/react-query';
import {
	cloneDeep,
	removeEmptyArrays,
} from '@utils/functions';
import { TMeasurabilityProjectQueryParams } from '@features/catalogs/meausrability-projects/list/types';
import { TQueryParams } from '@global-types/index';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */
export const useProducers = () => {
	/**************************** Refs ****************************/
	const containerRef = useRef<HTMLDivElement | null>(
		null
	);

	/**************************** States ****************************/
	const [resetFilterProducers, setResetFilterProducers] =
		useState(false);
	const [openDrawer, setOpenDrawer] = useState(false);
	const [
		openDrawerAddProducer,
		setOpenDrawerAddProducer,
	] = useState(false);
	const [
		openDrawerEditProducer,
		setOpenDrawerEditProducer,
	] = useState(false);

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);
	const [
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
	] = useState(false);

	const [isOverflowFilters] = useState(false);

	const {
		producersPage,
		producersPageSize,
		setProducersPage,
		ProducersSelectedRecords,
		setProducersSelectedRecords,
		setProducersOpenConfirmationModalDelete,
		ProducersQueryParams,
		setProducersQueryParams,
		producersSortStatus,
		setProducersSortStatus,
		setProducersSortParams,
		producersSortParams,
		setProducersPageSize,
		setProducersSelectedRow,
		producersLastAccessTokenCreated,
		setProducersLastAccessTokenCreated,
		producersDatatableOptions,
		setProducersDatatableOptions,
		producersQueryParams,
		producersSelectedRow,
		producersOpenConfirmationModalDelete,
		producersSelectedRecords,
		producersWithoutContactDatatableOptions,
		setProducersWithoutContactDatatableOptions,
	} = useStore();
	const { checkAcl, checkAccess } = useAuthorization();
	const { ref, width: widthContainer } = useElementSize();
	/**************************** Drawer show more production ****************************/
	const handleAddItem = () => {
		setOpenDrawerAddProducer(true);
	};

	const handleDeleteMultipleItems = () => {
		setOpenSelectedItemsToDeleteModal(true);
	};

	/**************************** Reset ****************************/
	const handleReset = () => {
		setProducersPageSize(10);
		setProducersQueryParams({});
		setProducersLastAccessTokenCreated(null);
		setProducersPage(1);
		setProducersSortParams('-createdAt');
		setProducersSortStatus({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/**************************** Actions ****************************/
	const handleEditProducerDrawer = (
		row: TParsedProducer
	) => {
		handleEdit(row);
		setOpenDrawer(false);
	};

	const handleEdit = (row: any) => {
		setProducersSelectedRow(row);
		setOpenDrawerEditProducer(true);
	};

	const handleCloseEditProducerDrawer = () => {
		setOpenDrawerEditProducer(false);
	};

	const handleChangeObfuscateIPFS = (
		row: any,
		obfuscateIPFS: any
	) => {
		obfuscateIPFS.mutate({
			id: row.id,
			obfuscateIPFS: !row.obfuscateIPFS,
		});
	};

	const handleCountryFilter = (
		_countries: TSelectOption[]
	) => {
		setProducersPage(1);

		const countries = _countries.map(
			(_country: TSelectOption) => _country.value
		);

		if (countries.length == 0) {
			const _offsetMesurabilityProjectsQueryParams =
				cloneDeep(producersQueryParams);
			delete _offsetMesurabilityProjectsQueryParams[
				'location.countryCode'
			];
			setProducersQueryParams(
				_offsetMesurabilityProjectsQueryParams
			);
			return;
		}
		const queryParamsCountries = {
			'location.countryCode': {
				$in: countries,
			},
		};
		const filterParams: TMeasurabilityProjectQueryParams =
			removeEmptyArrays({
				...producersQueryParams,
				...queryParamsCountries,
			});
		setProducersQueryParams(filterParams);
	};

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};
	const handleSortParams = (_column: TSortStatus) => {
		let sortParams = _column.columnAccessor;

		switch (_column.columnAccessor) {
			case 'producer':
				sortParams = 'entity';
				break;
			case 'contactName':
				sortParams = 'contact.firstName';
				break;
			case 'contactEmail':
				sortParams = 'contact.email';
				break;
			case 'place':
				sortParams = 'location.place';
				break;
			case 'contactPhone':
				sortParams = 'contact.phone';
				break;
			case 'countryCode':
				sortParams = 'location.countryCode';
				break;
			default:
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setProducersSortParams(sortParams);
		} else {
			setProducersSortParams('-' + sortParams);
		}
	};

	const handleOrganizationFilter = (
		organizations: TSelectOption[]
	) => {
		setProducersSelectedRecords([]);
		setProducersPage(1);
		const queryParamsProjects = {
			organization: organizations.map(
				(_organization: TSelectOption) =>
					_organization.value
			),
			site: [],
		};

		setProducersQueryParams({
			...ProducersQueryParams,
			...queryParamsProjects,
		});
	};

	/*************************************** Delete one Item  ***************************************/

	const handleConfirmDeleteItem = (
		remove: UseMutationResult<
			TApiResponseProducerPopulated,
			TApiError,
			string,
			unknown
		>
	) => {
		setProducersOpenConfirmationModalDelete({
			open: false,
			params: {},
		});

		remove.mutate(
			producersOpenConfirmationModalDelete.params.id
		);
		setOpenDrawer(false);
		setProducersSelectedRecords([]);
		setProducersLastAccessTokenCreated(null);
	};

	const handleCloseConfirmationDeleteItem = () => {
		setProducersOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
	};

	const handleOpenConfirmationDeleteItem = () => {
		setProducersOpenConfirmationModalDelete({
			open: true,
			params: producersSelectedRow,
		});
	};

	const handleapplystatusfilter = (status: any) => {
		setProducersSelectedRecords([]);
		setProducersPage(1);
		const queryParamsVerified = {
			verified: status.map(
				(_status: any) => _status.value
			),
		};
		setProducersQueryParams({
			...producersQueryParams,
			...queryParamsVerified,
		});
	};

	const handleapplyipfsstatusfilter = (status: any) => {
		setProducersSelectedRecords([]);
		setProducersPage(1);
		const queryParamsVerified = {
			obfuscateIPFS: status.map(
				(_status: any) => _status.value
			),
		};
		setProducersQueryParams({
			...producersQueryParams,
			...queryParamsVerified,
		});
	};

	/*************************************** Delete multiple Items  ***************************************/

	const handleConfirmDeleteMultipleItem =
		(removeMultiple: {
			mutate: UseMutateFunction<
				TResponseDeleteMultiple,
				TApiError,
				TBodyDeleteMultiple,
				unknown
			>;
			isLoading: boolean;
			isIdle: boolean;
		}) => {
			const idsList = [...ProducersSelectedRecords].map(
				(_Producer) => _Producer.id
			);
			removeMultiple.mutate({
				ids: idsList,
			});

			setProducersSelectedRecords([]);
			setOpenSelectedItemsToDeleteModal(false);
		};

	/*************************************** Datatables  ***************************************/
	const handleViewMore = (row: TParsedProducer) => {
		setProducersSelectedRow(row);
		setOpenDrawer(true);
	};

	const handleRowClicked = (row: {
		record: TProducer;
	}) => {
		if (
			checkAcl(ACLS.PRODUCERS.READ) &&
			checkAccess({
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.ORGANIZATION_ADMIN,
					ROLES.ORGANIZATION_STAFF,
				],
			})
		) {
			setProducersSelectedRow(row.record);
			setOpenDrawer(true);
		}
	};

	const handleSelectedRecordsChange = (
		selectedRecords: TProducer
	) => {
		setProducersSelectedRecords(selectedRecords);
	};

	const handleSortStatusChange = (data: TSortStatus) => {
		handleSortParams(data);
		setProducersSortStatus(data);
	};

	/**************************** Pagination ****************************/

	const handlepaginationchangesize = function (
		_size: string | number
	) {
		setProducersPageSize(_size);
		setProducersPage(1);
	};

	const handlechangepagination = (data: number) => {
		setProducersPage(data);
	};

	const handleResetSelectedItems = () => {
		setProducersSelectedRecords([]);
	};

	const handleProducerFilter = (
		producers: TQueryParams[]
	) => {
		setProducersPage(1);
		const queryParamsProjects = {
			entity: producers.map(
				(_producer: TParams) => _producer.value
			),
		};

		setProducersQueryParams({
			...producersQueryParams,
			...queryParamsProjects,
		});
	};

	const handleTechnologyFilter = (
		_technologies: TSelectOption[]
	) => {
		setProducersPage(1);
		const queryParamsProjects = {
			technology: _technologies.map(
				(_technology: TSelectOption) => _technology.value
			),
		};

		const filterParams: TMeasurabilityProjectQueryParams =
			removeEmptyArrays({
				...producersQueryParams,
				...queryParamsProjects,
			});
		setProducersQueryParams(filterParams);
	};

	/********************************************************************************************/

	return {
		handleResetSelectedItems,
		handlepaginationchangesize,
		handlechangepagination,
		handleSortStatusChange,
		handleSelectedRecordsChange,
		handleRowClicked,
		handleViewMore,
		handleConfirmDeleteMultipleItem,
		handleOpenConfirmationDeleteItem,
		handleCloseConfirmationDeleteItem,
		handleConfirmDeleteItem,
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		setShowScrollFilters,
		openDrawerAddProducer,
		setOpenDrawerAddProducer,
		openDrawer,
		setOpenDrawer,
		handleOrganizationFilter,
		handleScroll,
		handleSortParams,
		containerRef,
		handleAddItem,
		handleDeleteMultipleItems,
		handleReset,
		ref,
		widthContainer,
		isOverflowFilters,
		producersPage,
		producersPageSize,
		producersSelectedRecords,
		setProducersSelectedRecords,
		producersOpenConfirmationModalDelete,
		setProducersOpenConfirmationModalDelete,
		producersQueryParams,
		producersSortStatus,
		producersSortParams,
		producersSelectedRow,
		producersLastAccessTokenCreated,
		setProducersLastAccessTokenCreated,
		producersDatatableOptions,
		setProducersDatatableOptions,
		handleCountryFilter,
		resetFilterProducers,
		setResetFilterProducers,
		handleProducerFilter,
		handleTechnologyFilter,
		handleapplystatusfilter,
		handleapplyipfsstatusfilter,
		handleChangeObfuscateIPFS,
		openDrawerEditProducer,
		setOpenDrawerEditProducer,
		handleCloseEditProducerDrawer,
		handleEditProducerDrawer,
		handleEdit,
		producersWithoutContactDatatableOptions,
		setProducersWithoutContactDatatableOptions,
	};
};
